import React from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_productSettingsTableSearchAndFilterBar.module.scss';
import { FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum } from '~utils/enums';
import { TSettingsFilterParams } from '~types/redux/state';

const ProductSettingsTableSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.setting.settings.filters);
    const productList = useAppSelector((state) => state.gold.allProducts);

    const dispatch = useAppDispatch();

    const setFilters = (params: TSettingsFilterParams) => dispatch(combinedActions.settingSetGetSettingsFilters(params));
    const handleSearchClick = () => dispatch(combinedActions.settingGetSettingsAttempt());
    const handleCancelClick = () => {
        dispatch(combinedActions.settingClearGetSettingsFilters());
        dispatch(combinedActions.settingGetSettingsAttempt());
    };

    const massagedProductsListOption = () => {
        if (!productList.length) return [];
        return productList.map((product) => ({
            label: product.productName,
            value: product.productName,
        }));
    };

    const isSilver = filters.metalType === MetalTypeEnum[MetalTypeEnum.Silver];

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Product Name:',
            type: 'dropdown' as const,
            dropdownParams: {
                value: filters.productName,
                setValue: (event) => setFilters({ ...filters, productName: event.target.value }),
                options: massagedProductsListOption(),
                optionSelected: !!productList.find((product) => product.productName === filters.productName),
                noOptionsMessage: 'No product found',
                className: Styles.input,
            },
        },
        {
            label: 'Fund Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: FundTypeEnum[FundTypeEnum.Islamic],
                        value: FundTypeEnum.Islamic,
                    },
                    {
                        label: FundTypeEnum[FundTypeEnum.Conventional],
                        value: FundTypeEnum.Conventional,
                    },
                ],
                value: filters.fundType as keyof typeof FundTypeEnum,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, fundType: event.target.value }),
                optionSelected: !!FundTypeEnum[filters.fundType as keyof typeof FundTypeEnum],
                noOptionsMessage: 'No transaction type found',
                className: Styles.input,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Gold],
                        value: MetalTypeEnum[MetalTypeEnum.Gold],
                    },
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Silver],
                        value: MetalTypeEnum[MetalTypeEnum.Silver],
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilters({
                        ...filters,
                        metalType: event.target.value,
                        goldType: '',
                        silverType: '',
                    }),
                optionSelected: !!filters.metalType,
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Physical / Paper:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: isSilver
                    ? [
                        {
                            label: SilverTypeEnum[SilverTypeEnum.PhysicalSilver]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: String(SilverTypeEnum.PhysicalSilver),
                        },
                        {
                            label: SilverTypeEnum[SilverTypeEnum.PaperSilver]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: String(SilverTypeEnum.PaperSilver),
                        },
                    ]
                    : [
                        {
                            label: GoldTypeEnum[GoldTypeEnum.PhysicalGold]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: String(GoldTypeEnum.PhysicalGold),
                        },
                        {
                            label: GoldTypeEnum[GoldTypeEnum.PaperGold]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: String(GoldTypeEnum.PaperGold),
                        },
                    ],

                value: isSilver
                    ? String(filters.silverType || '')
                    : String(filters.goldType || ''),

                setValue: (event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;

                    setFilters({
                        ...filters,
                        goldType: isSilver ? '' : value,
                        silverType: isSilver ? value : '',
                    });
                },

                optionSelected: isSilver
                    ? !!filters.silverType
                    : !!filters.goldType,

                noOptionsMessage: 'No type found',
                className: Styles.input,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
            gridColumns={4}
        />
    );
};

export default ProductSettingsTableSearchAndFilterBar;
